/**
 * @fileoverview
 * API 레이어 전용 쿠키 유틸리티
 *
 * NOTE: 레이어 격리 원칙에 따라 이 파일은 API 레이어 전용입니다.
 * App 레이어(`layers/apps/curator/src/middleware/auth/cookieUtils.ts`)와 동일한 구현이지만,
 * 레이어 간 직접 참조를 방지하기 위해 독립적으로 유지됩니다.
 * 두 레이어 간 일관성은 유지하되, 각 레이어의 요구사항에 맞게 구현됩니다.
 */

export interface CookieOptions {
  domain?: string;
  path?: string;
  maxAge?: number;
  secure?: boolean;
  sameSite?: 'Strict' | 'Lax' | 'None';
}

const getCookie = (cookieString: string, key: string): string => {
  const cookie = cookieString.split(';').find((c) => c.trim().startsWith(`${key}=`));

  if (!cookie) {
    return '';
  }

  return cookie.substring(cookie.indexOf('=') + 1).trim();
};

/**
 * 클라이언트 전용 쿠키 설정 함수
 * 클라이언트에서만 호출되므로 window 체크 불필요
 */
const setCookie = (key: string, value: string, options: CookieOptions = {}): void => {
  const parts: string[] = [`${key}=${value}`];

  if (options.domain) {
    parts.push(`domain=${options.domain}`);
  }
  if (options.path) {
    parts.push(`path=${options.path}`);
  }
  if (options.maxAge !== undefined) {
    parts.push(`max-age=${options.maxAge}`);
  }
  if (options.secure) {
    parts.push('secure');
  }
  if (options.sameSite) {
    parts.push(`SameSite=${options.sameSite}`);
  }

  document.cookie = parts.join('; ');
};

type CookieStore = {
  get: (key: string) => string;
  set: (key: string, value: string, options?: CookieOptions) => void;
};

/**
 * 쿠키 스토어 생성
 *
 * @param defaultOptions - 기본 쿠키 옵션
 * @param cookieString - 서버 사이드에서 사용할 쿠키 헤더 문자열 (클라이언트에서는 undefined)
 * @returns 쿠키 스토어 객체
 *   - 클라이언트: `{ get, set }` - document.cookie 읽기/쓰기
 *   - 서버: `{ get, set }` - 쿠키 헤더 문자열 읽기만 (set은 no-op, Set-Cookie 헤더 직접 반환)
 *
 * NOTE: 서버 사이드에서는 `set`을 호출하지 않습니다. 서버에서는 Set-Cookie 헤더를 직접 반환합니다.
 *
 * @example
 * ```typescript
 * // 클라이언트에서 사용
 * const store = createCookieStore({});
 * store.get('access_token'); // document.cookie에서 읽음
 * store.set('key', 'value'); // document.cookie에 설정
 *
 * // 서버에서 사용
 * const store = createCookieStore({}, request.headers.get('cookie') || '');
 * store.get('access_token'); // cookieString에서 읽음
 * // set은 호출하지 않음 (서버에서는 Set-Cookie 헤더 직접 반환)
 * ```
 */
export const createCookieStore = (
  defaultOptions: CookieOptions = {},
  cookieString?: string,
): CookieStore => {
  const isServer = typeof window === 'undefined';
  let currentCookieString = cookieString || '';

  const get = (key: string): string => {
    const source = isServer ? currentCookieString : document.cookie;
    return getCookie(source, key);
  };

  /**
   * 클라이언트 전용 쿠키 설정 함수
   * 서버 사이드에서는 no-op (서버에서는 Set-Cookie 헤더를 직접 반환하므로)
   */
  const set = (key: string, value: string, options: CookieOptions = {}): void => {
    if (isServer) {
      // 서버 사이드에서는 set을 사용하지 않음
      return;
    }
    setCookie(key, value, { ...defaultOptions, ...options });
    currentCookieString = document.cookie;
  };

  return { get, set };
};
